import { ConsoleState, findDraft, findSchema, findTemplate, routeKeyOf } from '@casa/domain';
import { destinationOf, FLOW_RESOURCE, routeAddress, RouteDef } from '@casa/domain';
import { parse, stringify } from 'yaml';

/** Where a member's generated stylesheet and schemas live, derived the same way everywhere. */
export const artefactPaths = (
  state: ConsoleState,
  route: RouteDef,
): { transform?: string; vendorSchema?: string; casaSchema?: string } => {
  const group = state.groups.find((candidate) => candidate.id === route.groupId);
  const template = findTemplate(group?.templateId ?? '', state);
  const member = template?.members.find((candidate) => candidate.name === route.name);
  const schema = findSchema(state, route.canonicalSchemaId);
  const stem = template?.flowDir && member ? `${template.flowDir}-${member.slug}` : undefined;

  return {
    transform: `transforms/${stem ?? route.id}-to-canonical.xsl`,
    vendorSchema: `schemas/vendor/${stem ?? route.id}.json`,
    casaSchema: schema ? `schemas/integration/${schema.code}.v${schema.version}.json` : undefined,
  };
};

/** Kaoto reads required properties from `parameters`, so every path parameter is named. */
const component = (uri: string, parameters: Record<string, unknown>) => ({ uri, parameters });

/**
 * The Camel route this Casa configuration describes.
 *
 * The data plane does not execute this document — it runs the published mapping through its own
 * generic ingress — so this is a faithful rendering of the configuration for reading on the
 * canvas, not a deployment artefact. Generating it from the model rather than shipping a static
 * file is what keeps the canvas and the studio from disagreeing.
 */
export const mappingId = (routeId: string, scope = 'shared'): string =>
  `kaoto-datamapper-${routeId}-${scope.replace(/:/g, '-')}`;

/** The platform's first topic. The data plane reads this name as whatever it is configured to use. */
export const INGEST_TOPIC = 'integration.events';

/** A document for a web address is handed to delivery in the same worker, not through a topic. */
export const DELIVERY_ENTRY = 'direct:platform-delivery';

/** The id that marks a step as the endpoint's destination, however it is drawn. */
export const DELIVERY_STEP = 'platform-delivery';

/**
 * Only the topic this platform writes *and* reads is compressed. A destination topic is consumed
 * by somebody else's service, and what leaves here should not assume the client on the other side
 * can read a particular codec.
 */
export const INTERNAL_COMPRESSION = 'lz4';

export const IDENTITY_FIELDS = ['tenantId', 'tenantName', 'businessId', 'businessName', 'vendorId'];

export const compileFlow = (state: ConsoleState, route: RouteDef, scope = 'shared'): string => {
  const paths = artefactPaths(state, route);
  const routeKey = routeKeyOf(state, route);
  const id = routeKey.replace(/\//g, '-');

  /*
   * The default is built from Camel's own components only: check the vendor's bill, accept it,
   * then map it and send it on. The platform's own nodes (the vendor contract, the tenant and
   * business written into the bill) are added on the canvas, for the endpoints that need them.
   */
  const ingress = [
    {
      to: {
        ...component('json-validator', { resourceUri: paths.vendorSchema }),
        description: "Check the vendor's bill has the agreed fields",
      },
    },
    {
      to: {
        ...component('kafka', {
          topic: INGEST_TOPIC,
          key: '${header.platformPartitionKey}',
          compressionCodec: INTERNAL_COMPRESSION,
        }),
        description: 'Accept · the vendor is answered once this is durable',
      },
    },
  ];

  /* Casa's format is checked inside the mapping, so an invalid result is the mapping's fault. */
  const mapping: unknown[] = [{ to: component('xslt-saxon', { resourceUri: paths.transform, useJsonBody: true }) }];
  if (paths.casaSchema) mapping.push({ to: component('json-validator', { resourceUri: paths.casaSchema }) });

  /*
   * A Kafka destination is written to directly, keyed by endpoint and shop so one shop's documents
   * stay in order on one partition. A web address is drawn as the plain https call it is; the data
   * plane sends it through its delivery, which picks a shop's own address and keeps the secret
   * headers out of the flow. With no destination chosen yet, delivery says so rather than dropping.
   */
  const sink = destinationOf(state, route);
  const destination =
    sink?.kind === 'kafka' && sink.target.trim()
      ? {
          to: {
            ...component('kafka', { topic: sink.target.trim(), key: '${header.platformPartitionKey}' }),
            description: `Send to ${sink.target.trim()}`,
          },
        }
      : {
          to: {
            id: DELIVERY_STEP,
            uri: sink?.kind === 'http' && sink.target.trim() ? sink.target.trim() : DELIVERY_ENTRY,
            description: sink?.target.trim()
              ? `Send to ${sink.target.trim()}`
              : "Send to Casa's bill topic, set in Settings",
          },
        };

  const worker: unknown[] = [
    {
      step: {
        id: mappingId(route.id, scope),
        description: "Map the vendor's bill to Casa's format",
        steps: mapping,
      },
    },
    destination,
  ];

  return stringify(
    [
      {
        route: {
          id: `${id}-ingress`,
          description: `${route.name} · ${route.vendorId}. Runs while the vendor waits.`,
          from: {
            ...component('platform-http', {
              path: routeAddress(state, route).path,
              httpMethodRestrict: 'POST',
              consumes: route.format === 'xml' ? 'application/xml' : 'application/json',
            }),
            steps: ingress,
          },
        },
      },
      {
        route: {
          id: `${id}-worker`,
          description: `${route.name} · ${route.vendorId}. Runs after the vendor has gone.`,
          from: {
            ...component('kafka', { topic: INGEST_TOPIC, groupId: 'platform-worker' }),
            steps: worker,
          },
        },
      },
    ],
    { lineWidth: 0 },
  );
};

export interface FlowFacts {
  ingressPath?: string;
  method?: string;
}

const uriOf = (value: unknown): { uri?: string; parameters?: Record<string, unknown> } => {
  if (typeof value === 'string') return { uri: value };
  if (value && typeof value === 'object') return value as { uri?: string; parameters?: Record<string, unknown> };

  return {};
};

/**
 * Reads back the parts of a Camel flow that the Casa model owns, so an edit made on the canvas
 * can be applied to the configuration rather than lost. Only the ingress and the sink round
 * trip: anything else a person changes there has no field to be stored in.
 */
export const readFlow = (source: string): FlowFacts => {
  let parsed: unknown;
  try {
    parsed = parse(source);
  } catch {
    return {};
  }

  if (!Array.isArray(parsed)) return {};

  const facts: FlowFacts = {};

  for (const entry of parsed) {
    const definition = (entry as { route?: { from?: unknown } }).route;
    if (!definition?.from) continue;

    const from = uriOf(definition.from);
    if (from.uri?.startsWith('platform-http')) {
      const path = from.parameters?.path ?? from.uri.replace(/^platform-http:?/, '');
      if (typeof path === 'string' && path) facts.ingressPath = path;
      const method = from.parameters?.httpMethodRestrict;
      if (typeof method === 'string' && method) facts.method = method;
    }
  }

  return facts;
};

/**
 * The flow that was drawn for this endpoint and scope, when one has been taken over.
 *
 * Its presence is what decides ownership. An endpoint without one shows the generated
 * projection and runs the stylesheet alone; an endpoint with one shows and runs what was drawn.
 * There is no separate flag, because a second place to say the same thing is a second place for
 * it to be wrong.
 */
export const authoredFlow = (state: ConsoleState, routeId: string, scope = 'shared'): string | undefined => {
  const flow = findDraft(state, routeId, scope)?.resources?.[FLOW_RESOURCE];

  return flow?.trim() ? flow : undefined;
};

/** Every canvas, on every scope, carries the address Casa's API docs give its endpoint. */
export const lockAddress = (state: ConsoleState, route: RouteDef, _scope: string, flow: string): string => {
  if (!route.ingress?.path?.trim()) return flow;
  try {
    const parsed = parse(flow) as { route?: { from?: { uri?: string; parameters?: Record<string, unknown> } } }[];
    const from = (Array.isArray(parsed) ? parsed : []).find((entry) =>
      entry?.route?.from?.uri?.startsWith('platform-http'),
    )?.route?.from;
    if (!from) return flow;
    from.parameters = { ...from.parameters, path: routeAddress(state, route).path };
    return stringify(parsed);
  } catch {
    return flow;
  }
};

export { flowIssues } from '@casa/domain';
